/**
 * Takes the list of supported experiments for the application and transforms them into an array of objects in the form:
 * {
 *   experimentKey: string,
 *   variationKey: string, // optional
 *   availableVariationKeys: [string],
 *   shouldFireImpression: bool,
 * }
 * If a given supported experiment has its experimentKey in both the siteConfigurationEnabledExperimentKeys (meaning the experiment is enabled for the site)
 * and assignedOptimizelyExperiments (meaning the user is a participant in the experiment), then shouldFireImpression will be set to true and
 * the variationKey will be set to the assigned variation as determined by optimizely.
 *
 * Otherwise, shouldFireImpression will be set to false and the variationKey will not be set.
 *
 * @param {*} supportedExperiments an array of supportedExperiments for the application
 * @param {*} siteConfigurationEnabledExperimentKeys an array of experimentKeys defined by site configs
 * @param {*} assignedOptimizelyExperiments an array of experiments in form { experimentKey, variationKey } as determined by users personalization context.
 */
export function buildAvailableOptimizelyExperiments({
  supportedExperiments = [],
  siteConfigurationEnabledExperimentKeys = [],
  assignedOptimizelyExperiments = [],
}) {
  return supportedExperiments.map(supportedExperiment => {
    if (
      siteConfigurationEnabledExperimentKeys.includes(
        supportedExperiment.experimentKey
      )
    ) {
      const optimizelyExperiment = assignedOptimizelyExperiments.find(
        e => e.experimentKey === supportedExperiment.experimentKey
      );
      if (
        optimizelyExperiment?.variationKey &&
        supportedExperiment.variationKeys.includes(
          optimizelyExperiment.variationKey
        )
      ) {
        // experiment is supported for site and user is in a valid test cell.
        return {
          experimentKey: optimizelyExperiment.experimentKey,
          variationKey: optimizelyExperiment.variationKey,
          availableVariationKeys: supportedExperiment.variationKeys,
          shouldFireImpression: true,
        };
      }
    }
    return {
      experimentKey: supportedExperiment.experimentKey,
      availableVariationKeys: supportedExperiment.variationKeys,
      shouldFireImpression: false,
    };
  });
}

/**
 * Returns a final array of experiments that a given user should be exposed too.
 *
 * Any experiment included in the debugOptimizelyExperimentOverrides parameter takes priority over the
 * same experiment's settings as defined by the availableOptimizelyExperiments.
 *
 * Naieve approach for now - filter out all availableOptimizelyExperiments that are present in the override array,
 * then spread the non-overrided experiments together with the overrided experiments from the debugOptimizelyExperimentOverrides parameter.
 *
 * @param {*} availableOptimizelyExperiments an array of experiments available for the application.
 * @param {*} debugOptimizelyExperimentOverrides an array of experiments which can override any given available experiment.
 */
export function buildActiveOptimizelyExperiments({
  availableOptimizelyExperiments = [],
  debugOptimizelyExperimentOverrides = [],
}) {
  const availableExperimentsWithoutOverrides =
    availableOptimizelyExperiments.filter(
      availableExperiment =>
        !debugOptimizelyExperimentOverrides.some(
          experimentOverride =>
            experimentOverride.experimentKey ===
            availableExperiment.experimentKey
        )
    );

  return [
    ...availableExperimentsWithoutOverrides,
    ...debugOptimizelyExperimentOverrides,
  ];
}
